import { debounce } from "lodash-es";
import { PureComponent, type ReactElement } from "react";
import { HeaderBar, HeaderDivider, Slider, TriggerButton, TriggerButtonGroup, TriggerButtonIntent } from "widgets";

import type { ChartLayoutState, ColumnAnnotation } from "@/graphql.ts";
import type { SelectedDataSeries } from "@/redux/modules/dataset-viewer.ts";
import { CHART_TYPE, type ChartType } from "@/settings/settings";

import ErrorBars from "../../assets/img/icons/error_bars.svg?react";
import FilterOutEmpty from "../../assets/img/icons/filter-out-empty.svg?react";
import StackedBarChart from "../../assets/img/icons/stacked-bar-chart.svg?react";
import TimeSeries from "../../assets/img/icons/time-series.svg?react";

export interface IChartToolbarProps {
  chartLayout: ChartLayoutState; // the current layout state
  isTimeSeriesToggleEnabled: boolean; // this property depends on the selected annotations
  selectedDataSeries: SelectedDataSeries[];
  selectedAnnotations: ColumnAnnotation[];

  onChartLayoutStateChanged: (chartLayout: any) => void;
}

class ChartToolbar extends PureComponent<IChartToolbarProps, { symbolSize: number }> {
  private static RANGE_DEBOUNCE_TIMER = 200;

  state = {
    symbolSize: this.props.chartLayout.symbolSize ?? 0,
  };

  onChartTypeChange = (chartType: ChartType) => this.props.onChartLayoutStateChanged({ chartType: chartType });
  onTimeSeriesSetupChange = (enabled: boolean) => this.props.onChartLayoutStateChanged({ timeSeries: enabled });
  onErrorBarsChange = (errorBars: boolean) => this.props.onChartLayoutStateChanged({ errorBars });
  onLinLogChange = (log: boolean) => this.props.onChartLayoutStateChanged({ isLogScale: log });
  onSymbolSizeChange = (value: number) => this.props.onChartLayoutStateChanged({ symbolSize: value });
  onFilterOutEmptyValuesChange = (filterOut: boolean) =>
    this.props.onChartLayoutStateChanged({ filterOutEmptyValues: filterOut });

  componentDidMount() {
    this.debouncedOnSymbolSizeChange = debounce(this.debouncedOnSymbolSizeChange, ChartToolbar.RANGE_DEBOUNCE_TIMER);
  }

  componentDidUpdate(prevProps: Readonly<IChartToolbarProps>) {
    if (prevProps.chartLayout.symbolSize != this.props.chartLayout.symbolSize) {
      this.setState({ symbolSize: this.props.chartLayout.symbolSize ?? this.state.symbolSize });
    }
  }

  debouncedOnSymbolSizeChange = (value: number) => {
    this.onSymbolSizeChange(value);
  };

  render(): ReactElement {
    const stackedBarSelected: boolean = this.props.chartLayout.chartType === CHART_TYPE.STACKED_BAR;
    const categoricalTopLevelCondition = !this.props.isTimeSeriesToggleEnabled;
    const noDataSeriesSelected: boolean = !this.props.selectedDataSeries || this.props.selectedDataSeries.length === 0;
    const lessThanTwoDataSeriesSelected: boolean =
      !this.props.selectedDataSeries || this.props.selectedDataSeries.length < 2;
    const failingComboWithLogScale: boolean = this.props.chartLayout.errorBars;

    const timeScaleLabel: string = this.props.chartLayout.timeSeries
      ? "Do Not Scale X-Axis Using Numerical Values"
      : "Scale X-Axis Using Numerical Values";
    const filterOutEmptyValuesButtonLabel = this.props.chartLayout.filterOutEmptyValues
      ? "Show the Empty Categories on the X-Axis"
      : "Hide the Empty Categories on the X-Axis";

    return (
      <div style={{ display: "flex", justifyContent: "center" }} className="chart-toolbar">
        <HeaderBar
          actions={[
            <TriggerButtonGroup key={"chart-buttons-group-1"}>
              <span id="data-viewer-chart-type-selection" className="hotspot-beacon" />
              <TriggerButton
                icon={"ssid_chart"}
                label={"Line Plot"}
                onPress={() => {
                  !noDataSeriesSelected && this.onChartTypeChange(CHART_TYPE.LINE);
                }}
                key="line-chart-button"
                id="line-chart-button"
                isDisabled={noDataSeriesSelected}
                intent={this.props.chartLayout.chartType === CHART_TYPE.LINE ? TriggerButtonIntent.Active : undefined}
              />
              <TriggerButton
                icon={"bar_chart"}
                label={"Bar Chart"}
                onPress={() => {
                  !noDataSeriesSelected && this.onChartTypeChange(CHART_TYPE.BAR);
                }}
                key="bar-chart-button"
                id="bar-chart-button"
                isDisabled={noDataSeriesSelected}
                intent={this.props.chartLayout.chartType === CHART_TYPE.BAR ? TriggerButtonIntent.Active : undefined}
              />
              <TriggerButton
                icon={<StackedBarChart style={{ width: 18, height: 17, position: "relative", top: 0.5 }} />}
                label={"Stacked Bar Chart"}
                onPress={() => {
                  if (!noDataSeriesSelected && !lessThanTwoDataSeriesSelected) {
                    this.onChartTypeChange(CHART_TYPE.STACKED_BAR);
                    this.onErrorBarsChange(false);
                    this.onLinLogChange(false);
                  }
                }}
                key="stacked-chart-button"
                id="stacked-chart-button"
                isDisabled={
                  noDataSeriesSelected ||
                  (lessThanTwoDataSeriesSelected ? "A stacked bar chart requires at least two data series" : null)
                }
                intent={
                  this.props.chartLayout.chartType === CHART_TYPE.STACKED_BAR ? TriggerButtonIntent.Active : undefined
                }
              />
            </TriggerButtonGroup>,
            // <HeaderDivider key={"data-divider-1"} />,
            <TriggerButtonGroup key={"error-bars-buttons"}>
              <TriggerButton
                icon={<ErrorBars style={{ width: 16, height: 17 }} />}
                label={"Error Bars"}
                onPress={() =>
                  !noDataSeriesSelected &&
                  !stackedBarSelected &&
                  this.onErrorBarsChange(!this.props.chartLayout.errorBars)
                }
                key="error-bars-button"
                id="error-bars-button"
                isDisabled={noDataSeriesSelected || stackedBarSelected}
                intent={this.props.chartLayout.errorBars ? TriggerButtonIntent.Active : undefined}
              />
            </TriggerButtonGroup>,
            // <HeaderDivider key={"data-divider-2"} />,
            <TriggerButtonGroup key={"chart-buttons-group-2"}>
              <TriggerButton
                icon={<>lin</>}
                label={"Linear Y-Axis (Condensing: Average)"}
                onPress={() => !noDataSeriesSelected && this.onLinLogChange(false)}
                id={"linear-scaling-button"}
                key={"linear-scaling-button"}
                isDisabled={noDataSeriesSelected}
                intent={!this.props.chartLayout.isLogScale ? TriggerButtonIntent.Active : undefined}
              />
              <TriggerButton
                icon={<>log</>}
                label={"Logarithmic Y-Axis (Condensing: Log-Average)"}
                onPress={() => !noDataSeriesSelected && !stackedBarSelected && this.onLinLogChange(true)}
                id={"log-scaling-button"}
                key={"log-scaling-button"}
                isDisabled={
                  noDataSeriesSelected ||
                  (stackedBarSelected ? "Logarithmic y-axis not possible with stacked bar chart" : null) ||
                  (failingComboWithLogScale ? "Logarithmic y-axis not possible when error bars are visible" : null)
                }
                intent={this.props.chartLayout.isLogScale ? TriggerButtonIntent.Active : undefined}
              />
            </TriggerButtonGroup>,
            // <HeaderDivider key={"data-divider-3"} />,
            <TriggerButtonGroup key={"time-series-buttons"}>
              <span id="data-viewer-time-series-toggle" className="hotspot-beacon" />
              <TriggerButton
                icon={<TimeSeries />}
                label={timeScaleLabel}
                onPress={() =>
                  !noDataSeriesSelected &&
                  !categoricalTopLevelCondition &&
                  this.onTimeSeriesSetupChange(!this.props.chartLayout.timeSeries)
                }
                key="time-series-button"
                id="time-series-button"
                isDisabled={
                  noDataSeriesSelected ||
                  (categoricalTopLevelCondition ? "The current x-axis cannot use numerical scaling" : null)
                }
                intent={this.props.chartLayout.timeSeries ? TriggerButtonIntent.Active : undefined}
              />
            </TriggerButtonGroup>,
            // <HeaderDivider key={"data-divider-4"} />,
            <TriggerButtonGroup key={"filter-out-empty-values-buttons"}>
              <TriggerButton
                icon={<FilterOutEmpty style={{ width: 17, height: 18 }} />}
                label={filterOutEmptyValuesButtonLabel}
                onPress={() => this.onFilterOutEmptyValuesChange(!this.props.chartLayout.filterOutEmptyValues)}
                key="filter-out-empty-values-button"
                id="filter-out-empty-values-button"
                isDisabled={
                  noDataSeriesSelected ||
                  (this.props.chartLayout.timeSeries
                    ? "Filtering out empty values is not possible in combination with x-axis scaling using numerical values"
                    : null)
                }
                intent={this.props.chartLayout.filterOutEmptyValues ? TriggerButtonIntent.Active : undefined}
              />
            </TriggerButtonGroup>,
            <HeaderDivider key={"data-divider-5"} />,
            <Slider
              key={`data-viewer-line-chart-slider`}
              value={[this.state.symbolSize]}
              minValue={0}
              maxValue={6}
              step={0.2}
              onChange={([value]) => {
                this.setState({ symbolSize: value });
                this.debouncedOnSymbolSizeChange(value);
              }}
              icon={"show_chart"}
              tooltip={(value) => value.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
              noHeader
              isDisabled={this.props.chartLayout.chartType !== CHART_TYPE.LINE}
            />,
          ]}
        />
      </div>
    );
  }
}

export default ChartToolbar;
